import React, { useEffect, useMemo, useState } from "react";
import { Button, Surface, Tabs } from "@heroui/react";
import { PortalCheckbox } from "../components/form/PortalCheckbox";
import { LoadingState } from "../components/LoadingState";
import { PortalFormSaveBar } from "../components/PortalFormSaveBar";
import { PortalTextField } from "../components/form/PortalTextField";
import { PortalTextArea } from "../components/form/PortalTextArea";
import { useLocale } from "../context/LocaleContext";
import { apiFetch } from "../lib/apiClient";
import { isFormDirty } from "../lib/formDirty";
import { navigatePortal } from "../lib/navigation";
import { showErrorToast, showSuccessToast } from "../lib/toast";
import { smtpDeliveryMode } from "../lib/smtpStatus";

const TEMPLATE_SECTIONS = [
  { key: "customer_confirmation", labelKey: "emails.confirmation" },
  { key: "decision_approved", labelKey: "emails.approved" },
  { key: "decision_rejected", labelKey: "emails.rejected" }
];

const EMPTY_TEMPLATES = {
  customer_confirmation: { enabled: true, subject: "", body_html: "" },
  decision_approved: { enabled: true, subject: "", body_html: "" },
  decision_rejected: { enabled: true, subject: "", body_html: "" }
};

function smtpComparable(value) {
  return {
    enabled: Boolean(value.enabled),
    address: value.address || "",
    port: Number(value.port || 587),
    username: value.username || "",
    from_email: value.from_email || "",
    authentication: value.authentication || "plain",
    enable_starttls: Boolean(value.enable_starttls),
    password: value.password || ""
  };
}

export function EmailsPage() {
  const { t } = useLocale();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("smtp");
  const [saving, setSaving] = useState(false);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpClearing, setSmtpClearing] = useState(false);
  const [sendingTestKey, setSendingTestKey] = useState(null);
  const [generatingKey, setGeneratingKey] = useState(null);
  const [regeneratingConfigKey, setRegeneratingConfigKey] = useState(null);
  const [inlinePreviews, setInlinePreviews] = useState({
    customer_confirmation: { loading: false, error: "", rendered: null },
    decision_approved: { loading: false, error: "", rendered: null },
    decision_rejected: { loading: false, error: "", rendered: null }
  });
  const [templates, setTemplates] = useState(EMPTY_TEMPLATES);
  const [initialTemplates, setInitialTemplates] = useState(EMPTY_TEMPLATES);
  const [brandIdentityPrompt, setBrandIdentityPrompt] = useState("");
  const [initialBrandIdentityPrompt, setInitialBrandIdentityPrompt] = useState("");
  const [brandIdentityMaxChars, setBrandIdentityMaxChars] = useState(8000);
  const [placeholders, setPlaceholders] = useState([]);
  const [premiumAiEnabled, setPremiumAiEnabled] = useState(false);
  const [shopNotificationEmail, setShopNotificationEmail] = useState("");
  const [smtpSettings, setSmtpSettings] = useState({
    enabled: false,
    address: "",
    port: 587,
    username: "",
    from_email: "",
    authentication: "plain",
    enable_starttls: true,
    configured: false,
    using_app_default: true,
    app_smtp_configured: false,
    app_mailer_from: "",
    password: ""
  });
  const [initialSmtpSettings, setInitialSmtpSettings] = useState(smtpSettings);

  const isDirty = useMemo(
    () =>
      isFormDirty(
        { templates, brandIdentityPrompt },
        { templates: initialTemplates, brandIdentityPrompt: initialBrandIdentityPrompt }
      ),
    [templates, initialTemplates, brandIdentityPrompt, initialBrandIdentityPrompt]
  );

  const isSmtpDirty = useMemo(
    () => isFormDirty(smtpComparable(smtpSettings), smtpComparable(initialSmtpSettings)),
    [smtpSettings, initialSmtpSettings]
  );
  const smtpMode = useMemo(() => smtpDeliveryMode(smtpSettings), [smtpSettings]);

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      apiFetch("/api/email_templates"),
      apiFetch("/api/settings"),
      apiFetch("/api/shop_smtp"),
      apiFetch("/api/billing")
    ])
      .then(([emailData, settingsData, smtpData, billingData]) => {
        if (cancelled) return;
        const next = { ...EMPTY_TEMPLATES, ...emailData.email_templates };
        setTemplates(next);
        setInitialTemplates(next);
        const maxChars = Number(emailData.brand_identity_max_chars) || 8000;
        setBrandIdentityMaxChars(maxChars);
        const brandPrompt = (emailData.brand_identity_prompt || "").slice(0, maxChars);
        setBrandIdentityPrompt(brandPrompt);
        setInitialBrandIdentityPrompt(brandPrompt);
        setPlaceholders(emailData.placeholders || []);
        setShopNotificationEmail(settingsData.shop?.notification_email || emailData.notification_email || "");
        const nextSmtp = { ...smtpSettings, ...smtpData, password: "" };
        setSmtpSettings(nextSmtp);
        setInitialSmtpSettings(nextSmtp);
        setPremiumAiEnabled(billingData?.premium_ai_unlocked === true);
      })
      .catch(() => {
        if (!cancelled) showErrorToast(t("emails.loadError"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [t]);

  const updateTemplate = (key, field, value) => {
    setTemplates((prev) => {
      const next = { ...prev[key], [field]: value };
      if (field === "subject" || field === "body_html") {
        const subject = field === "subject" ? value : prev[key]?.subject;
        const bodyHtml = field === "body_html" ? value : prev[key]?.body_html;
        if (!String(subject || "").trim() && !String(bodyHtml || "").trim()) {
          next.enabled = false;
        }
      }
      return { ...prev, [key]: next };
    });
  };

  const fetchInlinePreview = async (templateKey, options = {}) => {
    const { silent = false } = options;
    setInlinePreviews((prev) => ({
      ...prev,
      [templateKey]: { ...prev[templateKey], loading: true, error: "" }
    }));
    try {
      const result = await apiFetch("/api/email_templates/preview", {
        method: "POST",
        body: JSON.stringify({
          template_key: templateKey,
          send_test: false,
          brand_identity_prompt: brandIdentityPrompt,
          email_templates: { [templateKey]: templates[templateKey] }
        })
      });
      setInlinePreviews((prev) => ({
        ...prev,
        [templateKey]: {
          loading: false,
          error: "",
          rendered: result.rendered || { subject: "", body_html: "" }
        }
      }));
    } catch (error) {
      const message = error?.message || t("emails.previewLoadError");
      setInlinePreviews((prev) => ({
        ...prev,
        [templateKey]: { loading: false, error: message, rendered: null }
      }));
      if (!silent) showErrorToast(message);
    }
  };

  useEffect(() => {
    if (activeTab !== "templates") return undefined;
    const timeout = window.setTimeout(() => {
      TEMPLATE_SECTIONS.forEach((section) => fetchInlinePreview(section.key, { silent: true }));
    }, 450);
    return () => window.clearTimeout(timeout);
  }, [activeTab, templates, brandIdentityPrompt]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiFetch("/api/email_templates", {
        method: "PATCH",
        body: JSON.stringify({
          email_templates: templates,
          brand_identity_prompt: brandIdentityPrompt
        })
      });
      setInitialTemplates(templates);
      setInitialBrandIdentityPrompt(brandIdentityPrompt);
      showSuccessToast(t("emails.saved"));
    } catch {
      showErrorToast(t("emails.loadError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setTemplates(initialTemplates);
    setBrandIdentityPrompt(initialBrandIdentityPrompt);
  };

  const sendTest = async (templateKey) => {
    if (!shopNotificationEmail.trim()) {
      showErrorToast(t("emails.testRecipientMissing"));
      return;
    }
    setSendingTestKey(templateKey);
    try {
      const result = await apiFetch("/api/email_templates/preview", {
        method: "POST",
        body: JSON.stringify({
          template_key: templateKey,
          send_test: true,
          brand_identity_prompt: brandIdentityPrompt,
          email_templates: { [templateKey]: templates[templateKey] }
        })
      });
      showSuccessToast(t("emails.testSentTo", { email: result.sent_to || shopNotificationEmail }));
    } catch {
      showErrorToast(t("emails.loadError"));
    } finally {
      setSendingTestKey(null);
    }
  };

  const generateTemplate = async (templateKey) => {
    setGeneratingKey(templateKey);
    try {
      const result = await apiFetch("/api/email_templates/generate", {
        method: "POST",
        body: JSON.stringify({
          template_key: templateKey,
          brand_identity_prompt: brandIdentityPrompt,
          email_template: {
            subject: templates[templateKey]?.subject || "",
            body_html: templates[templateKey]?.body_html || ""
          }
        })
      });
      const generated = result.generated || {};
      setTemplates((prev) => ({
        ...prev,
        [templateKey]: {
          ...prev[templateKey],
          subject: generated.subject || prev[templateKey]?.subject || "",
          body_html: generated.body_html || prev[templateKey]?.body_html || ""
        }
      }));
      showSuccessToast(t("emails.aiGenerated"));
    } catch (error) {
      showErrorToast(error?.message || t("emails.aiGenerateError"));
    } finally {
      setGeneratingKey(null);
    }
  };

  const regenerateTemplateConfig = async (templateKey) => {
    setRegeneratingConfigKey(templateKey);
    try {
      const result = await apiFetch("/api/email_templates/generate", {
        method: "POST",
        body: JSON.stringify({
          template_key: templateKey,
          generation_mode: "config_json",
          brand_identity_prompt: brandIdentityPrompt,
          email_template: {
            subject: templates[templateKey]?.subject || "",
            body_html: templates[templateKey]?.body_html || ""
          }
        })
      });
      const generated = result.generated || {};
      setTemplates((prev) => ({
        ...prev,
        [templateKey]: {
          ...prev[templateKey],
          subject: generated.subject || prev[templateKey]?.subject || "",
          body_html: generated.body_html || prev[templateKey]?.body_html || ""
        }
      }));
      showSuccessToast(t("emails.aiConfigRegenerated"));
    } catch (error) {
      showErrorToast(error?.message || t("emails.aiGenerateError"));
    } finally {
      setRegeneratingConfigKey(null);
    }
  };

  const updateSmtp = (key, value) => setSmtpSettings((prev) => ({ ...prev, [key]: value }));

  const saveSmtpSettings = async () => {
    setSmtpSaving(true);
    try {
      const result = await apiFetch("/api/shop_smtp", {
        method: "PATCH",
        body: JSON.stringify({
          smtp: {
            enabled: Boolean(smtpSettings.enabled),
            address: smtpSettings.address,
            port: Number(smtpSettings.port || 587),
            username: smtpSettings.username,
            from_email: smtpSettings.from_email,
            authentication: smtpSettings.authentication || "plain",
            enable_starttls: Boolean(smtpSettings.enable_starttls),
            password: smtpSettings.password
          }
        })
      });
      const nextSmtp = { ...smtpSettings, ...result, password: "" };
      setSmtpSettings(nextSmtp);
      setInitialSmtpSettings(nextSmtp);
      showSuccessToast(t("emails.smtp.saved"));
    } catch (error) {
      showErrorToast(error?.message || t("emails.smtp.saveError"));
    } finally {
      setSmtpSaving(false);
    }
  };

  const testSmtpSettings = async () => {
    setSmtpTesting(true);
    try {
      const result = await apiFetch("/api/shop_smtp/test", { method: "POST" });
      showSuccessToast(t("emails.testSentTo", { email: result.sent_to || shopNotificationEmail }));
      const latest = await apiFetch("/api/shop_smtp");
      const nextSmtp = { ...smtpSettings, ...latest, password: "" };
      setSmtpSettings(nextSmtp);
      setInitialSmtpSettings(nextSmtp);
    } catch (error) {
      showErrorToast(error?.message || t("emails.smtp.testError"));
    } finally {
      setSmtpTesting(false);
    }
  };

  const clearSmtpSettings = async () => {
    setSmtpClearing(true);
    try {
      const result = await apiFetch("/api/shop_smtp", { method: "DELETE" });
      const nextSmtp = { ...smtpSettings, ...result, password: "" };
      setSmtpSettings(nextSmtp);
      setInitialSmtpSettings(nextSmtp);
      showSuccessToast(t("emails.smtp.cleared"));
    } catch {
      showErrorToast(t("emails.smtp.saveError"));
    } finally {
      setSmtpClearing(false);
    }
  };

  const smtpTestRecipient =
    smtpSettings.from_email || shopNotificationEmail || smtpSettings.app_mailer_from || "";

  if (loading) return <LoadingState label={t("common.loading")} />;

  return (
    <>
      <PortalFormSaveBar isDirty={isDirty} onSave={handleSave} onDiscard={handleDiscard} saving={saving} />
      <PortalFormSaveBar
        isDirty={isSmtpDirty}
        onSave={saveSmtpSettings}
        onDiscard={() => setSmtpSettings(initialSmtpSettings)}
        saving={smtpSaving}
      />

      <div className="portal-withdrawals-layout">
        <Tabs
          selectedKey={activeTab}
          onSelectionChange={(key) => setActiveTab(String(key))}
          orientation="vertical"
          className="portal-status-tabs w-full max-w-[220px]"
        >
          <Tabs.ListContainer>
            <Tabs.List
              aria-label="Email configuration"
              className="portal-status-tabs__list w-full *:w-full *:justify-start *:px-3 *:py-2 *:text-sm *:font-medium *:data-[selected=true]:text-accent-foreground"
            >
              <Tabs.Tab id="smtp">
                <Tabs.Indicator className="bg-accent" />
                {t("emails.tabs.smtp")}
              </Tabs.Tab>
              <Tabs.Tab id="templates">
                <Tabs.Indicator className="bg-accent" />
                {t("emails.tabs.templates")}
              </Tabs.Tab>
            </Tabs.List>
          </Tabs.ListContainer>
        </Tabs>

        <div className="portal-withdrawals-layout__content">
          {activeTab === "smtp" ? (
            <Surface className="portal-email-surface">
              <p
                className={`portal-banner ${
                  smtpMode === "missing" ? "portal-banner--warning" : "portal-banner--positive"
                }`}
              >
                {smtpMode === "custom"
                  ? t("emails.smtp.statusCustom", { email: smtpSettings.from_email || t("common.dash") })
                  : smtpMode === "app"
                    ? t("emails.smtp.statusApp", { email: smtpSettings.app_mailer_from || t("common.dash") })
                    : t("emails.smtp.statusMissing")}
              </p>

              <div className="portal-checkbox-row">
                <PortalCheckbox
                  isSelected={Boolean(smtpSettings.enabled)}
                  onChange={(checked) => updateSmtp("enabled", checked)}
                >
                  {t("emails.smtp.useCustom")}
                </PortalCheckbox>
              </div>

              <PortalTextField label={t("emails.smtp.address")} value={smtpSettings.address || ""} onChange={(v) => updateSmtp("address", v)} />
              <PortalTextField label={t("emails.smtp.port")} value={String(smtpSettings.port || 587)} onChange={(v) => updateSmtp("port", v)} />
              <PortalTextField label={t("emails.smtp.username")} value={smtpSettings.username || ""} onChange={(v) => updateSmtp("username", v)} />
              <PortalTextField label={t("emails.smtp.fromEmail")} value={smtpSettings.from_email || ""} onChange={(v) => updateSmtp("from_email", v)} />
              <PortalTextField label={t("emails.smtp.password")} type="password" value={smtpSettings.password || ""} onChange={(v) => updateSmtp("password", v)} />

              <div className="portal-checkbox-row">
                <PortalCheckbox
                  isSelected={Boolean(smtpSettings.enable_starttls)}
                  onChange={(checked) => updateSmtp("enable_starttls", checked)}
                >
                  {t("emails.smtp.starttls")}
                </PortalCheckbox>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" isPending={smtpTesting} onPress={testSmtpSettings}>
                  {t("emails.smtp.test")}
                </Button>
                <Button variant="ghost" isPending={smtpClearing} onPress={clearSmtpSettings}>
                  {t("emails.smtp.clear")}
                </Button>
              </div>

              <p className="text-sm text-[var(--muted)]">
                {smtpTestRecipient
                  ? t("emails.smtp.testUsesRecipientEmail", { email: smtpTestRecipient })
                  : t("emails.smtp.testRecipientMissingAny")}
              </p>
              {!shopNotificationEmail && (
                <Button variant="ghost" size="sm" onPress={() => navigatePortal("/portal/settings")}>
                  {t("emails.settingsLink")}
                </Button>
              )}
            </Surface>
          ) : (
            <div className="portal-page-stack">
              {!premiumAiEnabled && <p className="portal-banner portal-banner--warning">{t("emails.premiumRequiredForAi")}</p>}

              <div className="portal-banner portal-banner--info">
                <strong>{t("emails.placeholders")}</strong>
                <p className="m-0 mt-1 text-sm">{placeholders.map((p) => `{{${p}}}`).join(", ")}</p>
              </div>

              <div className="portal-banner portal-banner--info">
                <strong>{t("emails.testRecipient")}</strong>
                <p className="m-0 mt-1">{shopNotificationEmail || t("common.dash")}</p>
                <p className="m-0 mt-1 text-sm text-[var(--muted)]">{t("emails.testRecipientHelp")}</p>
                <Button variant="ghost" size="sm" className="mt-2" onPress={() => navigatePortal("/portal/settings")}>
                  {t("emails.settingsLink")}
                </Button>
              </div>

              <Surface className="portal-email-surface">
                <PortalTextArea
                  label={t("emails.brandIdentityPrompt")}
                  value={brandIdentityPrompt}
                  onChange={(v) => setBrandIdentityPrompt(v.slice(0, brandIdentityMaxChars))}
                />
              </Surface>

              {TEMPLATE_SECTIONS.map((section) => {
                const template = templates[section.key] || EMPTY_TEMPLATES[section.key];
                const previewState = inlinePreviews[section.key] || { loading: false, error: "", rendered: null };

                return (
                  <Surface key={section.key} className="portal-email-surface portal-email-template-card">
                    <div className="portal-email-template__header">
                      <h3 className="text-base font-semibold">{t(section.labelKey)}</h3>
                      <div className="portal-email-template__actions">
                        <Button
                          variant="secondary"
                          size="sm"
                          isPending={regeneratingConfigKey === section.key}
                          isDisabled={!premiumAiEnabled}
                          onPress={() => regenerateTemplateConfig(section.key)}
                        >
                          {t("emails.regenerateConfigJson")}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          isPending={generatingKey === section.key}
                          isDisabled={!premiumAiEnabled}
                          onPress={() => generateTemplate(section.key)}
                        >
                          {t("emails.generateWithAi")}
                        </Button>
                      </div>
                    </div>

                    <div className="portal-email-grid">
                      <div className="grid gap-4">
                        <div className="portal-checkbox-row">
                          <PortalCheckbox
                            isSelected={Boolean(template.enabled)}
                            onChange={(checked) => updateTemplate(section.key, "enabled", checked)}
                          >
                            {t("emails.enabled")}
                          </PortalCheckbox>
                        </div>
                        <PortalTextField
                          label={t("emails.subject")}
                          value={template.subject || ""}
                          onChange={(v) => updateTemplate(section.key, "subject", v)}
                        />
                        <PortalTextArea
                          label={t("emails.body")}
                          value={template.body_html || ""}
                          onChange={(v) => updateTemplate(section.key, "body_html", v)}
                        />
                        <Button
                          variant="secondary"
                          size="sm"
                          className="portal-email-template__send-test"
                          isPending={sendingTestKey === section.key}
                          isDisabled={!shopNotificationEmail.trim()}
                          onPress={() => sendTest(section.key)}
                        >
                          {t("emails.sendTest")}
                        </Button>
                      </div>

                      <div className="portal-email-preview">
                        <div className="portal-email-preview__topbar">
                          <span className="portal-email-preview__dot" />
                          <span className="portal-email-preview__dot" />
                          <span className="portal-email-preview__dot" />
                          <span className="portal-email-preview__meta">{t("emails.preview")}</span>
                        </div>
                        <div className="portal-email-preview__header">
                          <p className="portal-email-preview__subject">
                            {previewState.rendered?.subject || template.subject || t("common.dash")}
                          </p>
                          <p className="portal-email-preview__sender">
                            {smtpSettings.from_email || smtpSettings.app_mailer_from || shopNotificationEmail || t("common.dash")}
                          </p>
                        </div>
                        <div className="portal-email-preview__body">
                          {previewState.loading ? (
                            <p className="text-sm text-[var(--muted)]">{t("common.loading")}</p>
                          ) : previewState.error ? (
                            <p className="text-sm text-red-800">{previewState.error}</p>
                          ) : previewState.rendered ? (
                            <div
                              className="portal-email-preview__rendered"
                              dangerouslySetInnerHTML={{ __html: previewState.rendered.body_html || "" }}
                            />
                          ) : (
                            <p className="text-sm text-[var(--muted)]">{t("emails.previewLoadError")}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </Surface>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
